import { useEffect, useRef, useState } from 'react'
import { useAuth } from '../../auth/AuthContext'
import { MultiSelectDropdown } from './MultiSelectDropdown'
import {
  fetchSubscriptions,
  createSubscription,
  deleteSubscription,
  fetchProfile,
  saveProfile,
  sendTestNotification,
  sendPortfolioReport,
  detectTelegramChatId,
} from '../../api/subscription'
import type {
  Subscription,
  SubscriptionEventType,
  SubscriptionChannel,
  CreateSubscriptionRequest,
  UserProfile,
  NotifyResult,
} from '../../types/subscription'

const DEFAULT_COOLDOWN_SECONDS = 3600
const DEFAULT_EVENT_TYPES: SubscriptionEventType[] = ['HF_WARNING']

const EVENT_TYPES: SubscriptionEventType[] = [
  'HF_WARNING',
  'HF_CRITICAL',
  'LIQUIDATION_RISK',
  'LP_OUT_OF_RANGE',
  'LP_NEAR_EDGE',
  'NEGATIVE_CARRY',
  'DELEVERAGE_REQUIRED',
  'HIGH_BORROW_UTILIZATION',
  'DAILY_REPORT',
  'HOURLY_UPDATE',
]

// Email addresses are case-insensitive; Telegram chat IDs are compared as typed.
function sameDestination(channel: SubscriptionChannel, a: string, b: string): boolean {
  const norm = (v: string) => (channel === 'EMAIL' ? v.trim().toLowerCase() : v.trim())
  return norm(a) === norm(b)
}

// Report subscriptions run on a fixed backend schedule; the cooldown field doesn't apply to them.
const REPORT_EVENT_TYPES: ReadonlySet<SubscriptionEventType> = new Set(['DAILY_REPORT', 'HOURLY_UPDATE'])

function cadenceLabel(sub: Subscription): string {
  if (sub.eventType === 'HOURLY_UPDATE') return 'hourly'
  if (sub.eventType === 'DAILY_REPORT') return 'daily'
  return `every ${sub.cooldownSeconds / 60}m`
}

function eventLabel(t: SubscriptionEventType): string {
  return t.replace(/_/g, ' ')
}

// ── Profile settings section ─────────────────────────────────────────────────

function DispatchResult({ result }: { result: NotifyResult }) {
  if (result.dispatched === 0) {
    return <span className="text-amber-400 text-xs">No channels dispatched</span>
  }
  return (
    <span className="text-accent-green text-xs">
      Sent to {result.dispatched} channel{result.dispatched !== 1 ? 's' : ''} ✓
    </span>
  )
}

// Shape of a @BotFather token (mirrors the backend's TelegramBotTokenFormat) — auto-detect only
// fires once the pasted value looks complete, so half-typed tokens never hit the API.
const TELEGRAM_TOKEN_PATTERN = /^\d+:[A-Za-z0-9_-]{30,}$/
const AUTO_DETECT_DELAY_MS = 400
// After a detect finds no message yet, keep checking until the user has pressed Start in the bot:
// every POLL_INTERVAL_MS, for at most POLL_MAX_MS.
const CHAT_ID_POLL_INTERVAL_MS = 3000
const CHAT_ID_POLL_MAX_MS = 120_000

function ProfileSection({
  portfolioId,
  profile,
  onSaved,
}: {
  portfolioId: string
  profile: UserProfile | null
  onSaved: (p: UserProfile) => void
}) {
  const [telegramToken, setTelegramToken] = useState('')
  const [telegramChatId, setTelegramChatId] = useState(profile?.telegramChatId ?? '')
  const [email, setEmail] = useState(profile?.notificationEmail ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const [detecting, setDetecting] = useState(false)
  const [detectError, setDetectError] = useState<string | null>(null)
  const [tokenSavedBeforeDetect, setTokenSavedBeforeDetect] = useState(false)
  // True while we are waiting for the user's first message to the bot (see startWatching).
  const [watching, setWatching] = useState(false)
  const watchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // End of the current watch window; kept across restarts so clicking Save/Auto-detect while
  // waiting cannot stretch the 2-minute cap. Cleared when the watch ends.
  const watchDeadlineRef = useRef<number | null>(null)
  // The background check currently running, if any. Save / Auto-detect wait for it instead of
  // being swallowed by the shared busy flag.
  const pollInFlightRef = useRef<Promise<boolean> | null>(null)
  // The chat ID that detection (not the user) last put in the field: if a replacement bot token is
  // saved while it is still showing, it belongs to the OLD bot and must not be saved with the new token.
  const detectedChatIdRef = useRef('')

  // Latest-value refs: timers and async continuations must see current state, not the render
  // they were created in. `busyRef` is the single in-flight guard shared by Save, the button and
  // the auto-detect timer (state updates are async, so state alone can't serialise them).
  const busyRef = useRef(false)
  const mountedRef = useRef(true)
  const chatIdRef = useRef(telegramChatId)
  const hasTokenRef = useRef(false)
  // The token (if any) that has already been through a detect attempt — automatic or manual —
  // so neither the effect nor handleSave's follow-up detect repeats it.
  const autoDetectedFor = useRef('')
  const portfolioIdRef = useRef(portfolioId)
  // Refs are synced in effects (not during render). After a commit there is a sub-millisecond gap
  // before they update; every reader is a timer (>= 400 ms) or a post-network continuation, so it
  // cannot observe that gap.
  useEffect(() => { chatIdRef.current = telegramChatId }, [telegramChatId])
  useEffect(() => { hasTokenRef.current = profile?.hasTelegramToken ?? false }, [profile?.hasTelegramToken])
  // The parent remounts this form per wallet (key={portfolioId}), so all refs start fresh for each
  // wallet; portfolioIdRef + isCurrent stay as defence in depth for late responses.
  useEffect(() => { portfolioIdRef.current = portfolioId }, [portfolioId])
  // Short-lived "✓ saved"-style indicators: tracked so unmounting (wallet switch) cancels them.
  const indicatorTimers = useRef<ReturnType<typeof setTimeout>[]>([])
  const flashFor = (ms: number, clear: () => void) => {
    indicatorTimers.current.push(setTimeout(() => { if (mountedRef.current) clear() }, ms))
  }
  useEffect(() => {
    mountedRef.current = true
    const timers = indicatorTimers.current
    return () => {
      mountedRef.current = false
      timers.forEach(clearTimeout)
      if (watchTimerRef.current) clearTimeout(watchTimerRef.current)
    }
  }, [])
  // An async continuation may only touch this form while it is still mounted AND still showing
  // the portfolio it started for (the component re-renders in place when the wallet switches).
  const isCurrent = (startedFor: string) => mountedRef.current && portfolioIdRef.current === startedFor

  const stopWatching = () => {
    if (watchTimerRef.current) clearTimeout(watchTimerRef.current)
    watchTimerRef.current = null
    watchDeadlineRef.current = null
    if (mountedRef.current) setWatching(false)
  }

  // Lets a user action (Save / Auto-detect) queue behind an in-flight background check rather
  // than bail out silently on the shared busy flag. Returns false if the form is busy with
  // another user action. On success the caller owns busyRef (it sets it here, in the same
  // synchronous step as the check, so a double click cannot slip past) and must release it.
  const claimForm = async (): Promise<boolean> => {
    if (busyRef.current && !pollInFlightRef.current) return false
    if (pollInFlightRef.current) await pollInFlightRef.current
    if (busyRef.current) return false
    busyRef.current = true
    return true
  }

  // Persists a detected chat ID. An ID the user typed meanwhile wins over the detected one.
  const applyDetectedChatId = async (startedFor: string, detected: string) => {
    const chatId = chatIdRef.current.trim() || detected
    // Ref first: a Save queued behind this check reads it before React re-renders.
    chatIdRef.current = chatId
    if (chatId === detected) detectedChatIdRef.current = detected
    setTelegramChatId(chatId)
    try {
      const withChatId = await saveProfile(startedFor, {
        telegramBotToken: null,
        telegramChatId: chatId,
        notificationEmail: null,
      })
      if (isCurrent(startedFor)) onSaved(withChatId)
    } catch {
      if (isCurrent(startedFor)) setDetectError('Chat ID detected but not saved — click Save Profile')
    }
  }

  // Telegram reveals a chat ID only after the user has messaged the bot, so "no message yet" is
  // expected right after the token is saved. Keep re-checking on a timer so the ID fills in by
  // itself once the user presses Start — no further clicks. Stops on: found, a real error
  // (invalid token…), a chat ID typed by the user, a wallet switch/unmount, or the time cap.
  // Shares busyRef with Save/the button: a tick that finds the form busy simply waits one more beat.
  const startWatching = (startedFor: string) => {
    if (watchTimerRef.current) clearTimeout(watchTimerRef.current)
    watchTimerRef.current = null
    setWatching(true)
    watchDeadlineRef.current ??= Date.now() + CHAT_ID_POLL_MAX_MS
    const schedule = () => { watchTimerRef.current = setTimeout(tick, CHAT_ID_POLL_INTERVAL_MS) }
    // One check. Resolves true to keep waiting. busyRef is released inside the promise, so anyone
    // awaiting it (claimForm) sees the form free.
    const check = async (): Promise<boolean> => {
      try {
        const r = await detectTelegramChatId(startedFor)
        if (!isCurrent(startedFor)) return false
        if (r.chatId) {
          if (!chatIdRef.current.trim()) await applyDetectedChatId(startedFor, r.chatId)
          stopWatching()
          return false
        }
        if (r.code === 'NO_MESSAGES') return true
        setDetectError(r.error ?? 'Not found')
        stopWatching()
        return false
      } catch (err: unknown) {
        if (isCurrent(startedFor)) setDetectError(err instanceof Error ? err.message : 'Detection failed')
        stopWatching()
        return false
      } finally {
        busyRef.current = false
      }
    }
    const tick = async () => {
      watchTimerRef.current = null
      if (!isCurrent(startedFor) || chatIdRef.current.trim()) { stopWatching(); return }
      if (busyRef.current) {
        if (Date.now() >= (watchDeadlineRef.current ?? 0)) { stopWatching(); return }
        schedule()
        return
      }
      busyRef.current = true
      const run = check()
      pollInFlightRef.current = run
      const keepWaiting = await run
      pollInFlightRef.current = null
      if (!keepWaiting || !isCurrent(startedFor)) return
      if (chatIdRef.current.trim()) { stopWatching(); return }
      if (Date.now() >= (watchDeadlineRef.current ?? 0)) {
        setDetectError('No message from your bot yet — send it any message, then click Auto-detect')
        stopWatching()
        return
      }
      schedule()
    }
    schedule()
  }

  // A chat ID typed by hand ends the wait.
  useEffect(() => {
    if (telegramChatId.trim()) stopWatching()
  }, [telegramChatId])

  const handleDetectChatId = async () => {
    if (!(await claimForm())) return
    const startedFor = portfolioId
    const token = telegramToken.trim()
    try {
      busyRef.current = true
      setDetecting(true)
      setDetectError(null)
      setTokenSavedBeforeDetect(false)
      // `/chat-id` always reads the bot token from the saved profile (never from a request
      // param — a token belongs in persisted state, not a GET query string / access log). If
      // the user typed a new/replacement token but hasn't clicked "Save" yet, persist it first
      // so detection reads the token that's actually on screen instead of a stale/absent one.
      // Mirrors the auto-detect chain handleSave already runs after a normal save.
      if (token) {
        const updated = await saveProfile(startedFor, {
          telegramBotToken: token,
          telegramChatId: null,
          notificationEmail: null,
        })
        // Only a token persisted for the wallet still on screen counts as "tried": a failed save
        // stays retryable, and a late response for a wallet we switched away from must not mark
        // the new wallet as already tried.
        if (!isCurrent(startedFor)) return
        autoDetectedFor.current = token
        onSaved(updated)
        setTelegramToken('')  // clear token field after save (never re-display)
        // Confirm the persist independently of the detect outcome below — otherwise a
        // "Not found" (the expected result on a user's very first click, per the button's
        // own tooltip) reads as if nothing happened, when the token was in fact saved.
        setTokenSavedBeforeDetect(true)
        flashFor(5000, () => setTokenSavedBeforeDetect(false))
      }
      const r = await detectTelegramChatId(startedFor)
      if (!isCurrent(startedFor)) return
      if (r.chatId) {
        stopWatching()
        // Never clobber an ID the user typed while the request was in flight.
        if (!chatIdRef.current.trim()) await applyDetectedChatId(startedFor, r.chatId)
      } else if (r.code === 'NO_MESSAGES') {
        startWatching(startedFor)
      } else {
        stopWatching()
        setDetectError(r.error ?? 'Not found')
      }
    } catch (err: unknown) {
      if (isCurrent(startedFor)) setDetectError(err instanceof Error ? err.message : 'Detection failed')
    } finally {
      busyRef.current = false
      if (mountedRef.current) setDetecting(false)
    }
  }

  // Auto-detect once a complete-looking token is pasted, no chat ID is set, and no token is saved
  // yet — replacing an existing token never persists on paste; use Save or the button for that.
  // Each distinct token is tried once automatically; the button stays available for retries
  // (e.g. after the user has sent the bot its first message). Everything except the token is read
  // through refs at fire time, so the deps are deliberately just [telegramToken]: a chat ID typed
  // during the debounce window is honoured by the re-check below rather than by re-scheduling.
  useEffect(() => {
    const token = telegramToken.trim()
    if (!TELEGRAM_TOKEN_PATTERN.test(token) || autoDetectedFor.current === token) return
    const timer = setTimeout(() => {
      if ((busyRef.current && !pollInFlightRef.current) || chatIdRef.current.trim() || hasTokenRef.current) return
      void handleDetectChatId()
    }, AUTO_DETECT_DELAY_MS)
    return () => clearTimeout(timer)
  }, [telegramToken])

  const [testSending, setTestSending] = useState(false)
  const [testResult, setTestResult] = useState<NotifyResult | null>(null)
  const [reportSending, setReportSending] = useState(false)
  const [reportResult, setReportResult] = useState<NotifyResult | null>(null)
  const [dispatchError, setDispatchError] = useState<string | null>(null)

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!(await claimForm())) return
    const startedFor = portfolioId
    const token = telegramToken.trim()
    try {
      busyRef.current = true
      setSaving(true)
      setError(null)
      setDetectError(null)
      setSaved(false)
      // A replacement token invalidates a chat ID that was auto-detected for the previous bot
      // (it may have landed while this Save was queued behind a background check): drop it and
      // let the detection after this save find the right one. An ID the user typed is kept.
      if (token && chatIdRef.current.trim() && chatIdRef.current.trim() === detectedChatIdRef.current) {
        chatIdRef.current = ''
        setTelegramChatId('')
      }
      const updated = await saveProfile(startedFor, {
        telegramBotToken: token || null,
        telegramChatId: chatIdRef.current.trim() || null,
        notificationEmail: email.trim() || null,
      })
      if (!isCurrent(startedFor)) return
      onSaved(updated)
      setTelegramToken('')  // clear token field after save (never re-display)
      setSaved(true)
      flashFor(3000, () => setSaved(false))
      // No chat ID yet but a bot token exists (just typed, or saved earlier): look for the user's
      // message to the bot now, and if it hasn't arrived keep watching for it.
      if (!chatIdRef.current.trim() && (token || hasTokenRef.current || updated.hasTelegramToken)) {
        if (token) autoDetectedFor.current = token
        // The profile above is already saved: a failure here is a detection failure, not a save failure.
        try {
          const detected = await detectTelegramChatId(startedFor)
          if (!isCurrent(startedFor)) return
          if (detected.chatId) {
            stopWatching()
            await applyDetectedChatId(startedFor, detected.chatId)
          } else if (detected.code === 'NO_MESSAGES') {
            startWatching(startedFor)
          } else {
            stopWatching()
            setDetectError(detected.error ?? 'No messages found — send a message to your bot first')
          }
        } catch (err: unknown) {
          if (isCurrent(startedFor)) setDetectError(err instanceof Error ? err.message : 'Detection failed')
        }
      }
    } catch (err: unknown) {
      if (isCurrent(startedFor)) setError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      busyRef.current = false
      if (mountedRef.current) setSaving(false)
    }
  }

  const handleTest = async () => {
    const startedFor = portfolioId
    setTestSending(true)
    setTestResult(null)
    setDispatchError(null)
    try {
      const r = await sendTestNotification(startedFor)
      if (!isCurrent(startedFor)) return
      setTestResult(r)
      flashFor(8000, () => setTestResult(null))
    } catch (err: unknown) {
      if (isCurrent(startedFor)) setDispatchError(err instanceof Error ? err.message : 'Failed to send test')
    } finally {
      if (mountedRef.current) setTestSending(false)
    }
  }

  const handleReport = async () => {
    const startedFor = portfolioId
    setReportSending(true)
    setReportResult(null)
    setDispatchError(null)
    try {
      const r = await sendPortfolioReport(startedFor)
      if (!isCurrent(startedFor)) return
      setReportResult(r)
      flashFor(8000, () => setReportResult(null))
    } catch (err: unknown) {
      if (isCurrent(startedFor)) setDispatchError(err instanceof Error ? err.message : 'Failed to send report')
    } finally {
      if (mountedRef.current) setReportSending(false)
    }
  }

  const hasTelegram = profile?.hasTelegramToken ?? false
  const hasEmail = !!(profile?.notificationEmail)

  return (
    <form onSubmit={handleSave} className="mb-6 p-4 rounded-lg bg-surface border border-surface-card">
      <h3 className="text-sm font-semibold text-ink-soft mb-3">Notification Channels</h3>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-3">
        {/* Telegram bot token */}
        <div>
          <label htmlFor="telegram-bot-token" className="text-xs text-ink-muted mb-1 block">
            Telegram Bot Token
            {profile?.hasTelegramToken && (
              <span className="ml-2 text-accent-green text-xs">✓ configured</span>
            )}
          </label>
          <input
            id="telegram-bot-token"
            name="telegram-bot-token"
            type="password"
            value={telegramToken}
            onChange={e => setTelegramToken(e.target.value)}
            placeholder={profile?.hasTelegramToken ? 'Enter new token to replace…' : 'Paste token from @BotFather'}
            className="w-full bg-surface-card border border-surface rounded-lg px-3 py-2 text-sm text-ink placeholder-gray-600 focus:outline-none focus:border-accent-blue"
          />
          <p className="text-xs text-gray-400 mt-1">
            Create a bot via @BotFather on Telegram, paste the token here.
          </p>
        </div>

        {/* Telegram chat ID */}
        <div>
          <label htmlFor="telegram-chat-id" className="text-xs text-ink-muted mb-1 block">
            Telegram Chat ID
            {profile?.telegramChatId && (
              <span className="ml-2 text-accent-green text-xs">✓ {profile.telegramChatId}</span>
            )}
          </label>
          <div className="flex gap-2">
            <input
              id="telegram-chat-id"
              name="telegram-chat-id"
              type="text"
              value={telegramChatId}
              onChange={e => setTelegramChatId(e.target.value)}
              placeholder="-100xxxxxxxxxx"
              className="flex-1 bg-surface-card border border-surface rounded-lg px-3 py-2 text-sm text-ink placeholder-gray-600 focus:outline-none focus:border-accent-blue"
            />
            <button
              type="button"
              onClick={handleDetectChatId}
              disabled={saving || detecting}
              title="Send any message to your bot first, then click to auto-detect your chat ID (also runs automatically when you paste a bot token)"
              className="px-3 py-2 rounded-lg bg-surface-card border border-surface text-xs text-ink-soft hover:text-ink hover:border-accent-blue disabled:opacity-50 shrink-0"
            >
              {detecting ? 'Detecting…' : 'Auto-detect'}
            </button>
          </div>
          {tokenSavedBeforeDetect && (
            <p className="text-accent-green text-xs mt-1">Token saved ✓</p>
          )}
          {watching && (
            <p className="text-amber-400 text-xs mt-1" role="status" aria-live="polite">
              Waiting for your message — open your bot in Telegram and press Start. The ID fills in by itself.
            </p>
          )}
          {detectError && <p className="text-accent-red text-xs mt-1">{detectError}</p>}
          <p className="text-xs text-gray-400 mt-1">
            Your chat or channel ID — used for test &amp; report messages. Paste the token and save, then
            send your bot any message (press Start): the ID is detected and saved automatically.
          </p>
        </div>

        {/* Email */}
        <div>
          <label htmlFor="notification-email" className="text-xs text-ink-muted mb-1 block">Notification Email</label>
          <input
            id="notification-email"
            name="notification-email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="w-full bg-surface-card border border-surface rounded-lg px-3 py-2 text-sm text-ink placeholder-gray-600 focus:outline-none focus:border-accent-blue"
          />
          <p className="text-xs text-gray-400 mt-1">
            Receive daily reports and alerts by email.
          </p>
        </div>
      </div>

      {error && <p className="text-accent-red text-xs mb-2">{error}</p>}

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="submit"
          disabled={saving || detecting}
          className="px-4 py-2 rounded-lg bg-accent-blue text-ink text-sm font-medium hover:bg-accent-blue/80 disabled:opacity-50"
        >
          {saving ? 'Saving…' : saved ? 'Saved ✓' : 'Save Profile'}
        </button>

        {hasTelegram && (
          <button
            type="button"
            onClick={handleTest}
            disabled={testSending}
            className="px-4 py-2 rounded-lg bg-blue-600/30 border border-blue-500/40 text-blue-300 text-sm font-medium hover:bg-blue-600/50 disabled:opacity-50"
          >
            {testSending ? 'Sending…' : 'Send Test to Telegram'}
          </button>
        )}

        {(hasTelegram || hasEmail) && (
          <button
            type="button"
            onClick={handleReport}
            disabled={reportSending}
            className="px-4 py-2 rounded-lg bg-violet-600/30 border border-violet-500/40 text-violet-400 text-sm font-medium hover:bg-violet-600/50 disabled:opacity-50"
          >
            {reportSending ? 'Sending…' : 'Send Portfolio Report'}
          </button>
        )}

        {testResult && <DispatchResult result={testResult} />}
        {reportResult && <DispatchResult result={reportResult} />}
        {dispatchError && <span className="text-accent-red text-xs">{dispatchError}</span>}
      </div>
    </form>
  )
}

// ── Subscription list + add form ─────────────────────────────────────────────

export default function SubscriptionPanel() {
  const { portfolioId } = useAuth()
  const [subs, setSubs] = useState<Subscription[]>([])
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(false)
  // Wallet the loaded `profile` belongs to: the form must never be seeded from another wallet's data.
  const [profileReadyFor, setProfileReadyFor] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Soft, non-blocking notice (e.g. subscriptions were moved or merged after a chat ID change).
  const [notice, setNotice] = useState<string | null>(null)
  // Latest values for async continuations: state captured in a closure goes stale across the
  // two onSaved calls one save flow can make.
  const profileRef = useRef<UserProfile | null>(null)
  const subsRef = useRef<Subscription[]>([])
  useEffect(() => { profileRef.current = profile }, [profile])
  useEffect(() => { subsRef.current = subs }, [subs])

  // form state
  const [eventTypes, setEventTypes] = useState<SubscriptionEventType[]>(DEFAULT_EVENT_TYPES)
  const [channel, setChannel] = useState<SubscriptionChannel>('TELEGRAM')
  const [destination, setDestination] = useState('')
  const [cooldown, setCooldown] = useState(DEFAULT_COOLDOWN_SECONDS)
  const [adding, setAdding] = useState(false)
  const currentPortfolioRef = useRef(portfolioId)
  // Synchronous re-entrancy guard for handleAdd (state alone can't stop a fast double submit);
  // addRunRef invalidates it when the wallet changes mid-flight.
  const addingRef = useRef(false)
  const addRunRef = useRef(0)
  useEffect(() => { currentPortfolioRef.current = portfolioId }, [portfolioId])

  // Saving a different Telegram chat ID moves the wallet's matching Telegram subscriptions on the
  // server (and merges ones that now duplicate each other), so reload the list to show the result.
  const handleProfileSaved = (saved: UserProfile) => {
    const startedFor = portfolioId
    const chatIdChanged = (saved.telegramChatId ?? '') !== (profileRef.current?.telegramChatId ?? '')
    profileRef.current = saved   // synchronously: a second onSaved in the same flow must see this one
    setProfile(saved)
    if (!startedFor || !chatIdChanged) return
    const before = subsRef.current.length
    fetchSubscriptions(startedFor)
      .then(list => {
        if (currentPortfolioRef.current !== startedFor) return
        setSubs(list)
        const merged = before - list.length
        setNotice(merged > 0
          ? `Chat ID updated — ${merged} duplicate subscription${merged === 1 ? '' : 's'} merged into the new chat.`
          : null)
      })
      .catch(() => {
        if (currentPortfolioRef.current === startedFor) {
          setNotice('Chat ID saved, but the subscription list could not be refreshed — reload to see the current destinations.')
        }
      })
  }

  // Whether the user has manually edited `destination` since the last channel switch — once
  // true, the profile-sync effect below must never overwrite what they typed. Set on every
  // keystroke in the destination input, cleared whenever `channel` changes (see effect below).
  const destinationTouchedRef = useRef(false)

  // On a channel switch: reset the touched-flag and pre-populate destination from the profile
  // — saved notification email for EMAIL, saved Telegram chat ID for TELEGRAM (mirrors EMAIL;
  // previously TELEGRAM was always cleared to '', requiring the chat ID to be retyped here even
  // when already known from ProfileSection).
  useEffect(() => {
    destinationTouchedRef.current = false
    if (channel === 'EMAIL') {
      setDestination(profile?.notificationEmail ?? '')
    } else if (channel === 'TELEGRAM') {
      setDestination(profile?.telegramChatId ?? '')
    }
  }, [channel])

  // Keep destination synced if the profile arrives or changes asynchronously (e.g. the initial
  // fetch resolving after first render — TELEGRAM is the default channel, so this race matters
  // there too, not just for EMAIL) while the matching channel is already selected. Gated on
  // destinationTouchedRef so an unrelated profile update (e.g. ProfileSection persisting a token
  // via its "Detect" button) never overwrites a value the user has since typed themselves —
  // channel alone isn't a safe-enough guard for that, per an earlier EMAIL-only version of this
  // effect having exactly this gap.
  useEffect(() => {
    if (destinationTouchedRef.current) return
    if (channel === 'EMAIL' && profile?.notificationEmail) {
      setDestination(profile.notificationEmail)
    } else if (channel === 'TELEGRAM' && profile?.telegramChatId) {
      setDestination(profile.telegramChatId)
    }
  }, [profile, channel])

  useEffect(() => {
    // Drop everything that belongs to the previous wallet first — also when portfolioId becomes
    // empty — so nothing of it can render, seed a form or be saved against the next one.
    // Defensive: today App.tsx unmounts this panel on logout/session expiry instead of changing
    // portfolioId in place, so this reset is not fixing a live race — it keeps the panel safe if
    // an in-place wallet switch is ever added.
    setSubs([])
    setNotice(null)
    setProfile(null)
    setProfileReadyFor(null)
    setError(null)
    setLoading(false)
    setDestination('')
    destinationTouchedRef.current = false
    // The add form belongs to the wallet too; an add still in flight for the old one is abandoned
    // (its completion is ignored via currentPortfolioRef) and must not keep this form locked.
    addRunRef.current += 1
    addingRef.current = false
    setAdding(false)
    setEventTypes(DEFAULT_EVENT_TYPES)
    setChannel('TELEGRAM')
    setCooldown(DEFAULT_COOLDOWN_SECONDS)
    if (!portfolioId) return
    let cancelled = false   // a late response for a previous wallet must not land on this one
    setLoading(true)
    // Independent sources: one failing must not hide what the other loaded.
    Promise.allSettled([fetchSubscriptions(portfolioId), fetchProfile(portfolioId)])
      .then(([subsResult, profileResult]) => {
        if (cancelled) return
        const failures: string[] = []
        const reason = (r: PromiseRejectedResult) => (r.reason instanceof Error ? r.reason.message : String(r.reason))
        if (subsResult.status === 'fulfilled') setSubs(subsResult.value); else failures.push(`subscriptions: ${reason(subsResult)}`)
        if (profileResult.status === 'fulfilled') setProfile(profileResult.value); else failures.push(`profile: ${reason(profileResult)}`)
        if (failures.length > 0) setError(failures.join('; '))
      })
      .catch(e => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load notifications') })
      .finally(() => {
        if (cancelled) return
        // Whatever happened above, never leave the skeleton up or the form hidden.
        setProfileReadyFor(portfolioId)   // the form shows once loaded — empty if the profile load failed
        setLoading(false)
      })
    return () => { cancelled = true }
  }, [portfolioId])

  // Events already subscribed for the chosen channel + destination: not offered again.
  const alreadySubscribed = new Set<SubscriptionEventType>(
    subs
      .filter(sub => sub.channel === channel && sameDestination(channel, sub.destination, destination))
      .map(sub => sub.eventType),
  )
  // The backend rejects HOURLY_UPDATE on EMAIL (Telegram only), so it isn't offered there.
  const unavailable = new Set<SubscriptionEventType>(alreadySubscribed)
  const unavailableNotes = new Map<SubscriptionEventType, string>()
  alreadySubscribed.forEach(t => unavailableNotes.set(t, 'subscribed'))
  if (channel === 'EMAIL' && !unavailable.has('HOURLY_UPDATE')) {
    unavailable.add('HOURLY_UPDATE')
    unavailableNotes.set('HOURLY_UPDATE', 'Telegram only')
  }
  const cooldownApplies = !(eventTypes.length > 0 && eventTypes.every(t => REPORT_EVENT_TYPES.has(t)))
  const toCreate = eventTypes.filter(t => !unavailable.has(t))

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    if (addingRef.current || !portfolioId || !destination.trim() || toCreate.length === 0) return
    const startedFor = portfolioId
    const run = addRunRef.current
    addingRef.current = true
    try {
      setAdding(true)
      setError(null)
      const dest = destination.trim()
      // One subscription per selected event (the API creates them one at a time).
      const results = await Promise.allSettled(
        toCreate.map(eventType => {
          const req: CreateSubscriptionRequest = { eventType, channel, destination: dest, cooldownSeconds: cooldown }
          return createSubscription(startedFor, req)
        }),
      )
      // Same relevance test as the lock release below: right wallet AND no reset since this started
      // (switching away and back must not apply a stale completion to the fresh session).
      if (currentPortfolioRef.current === startedFor && addRunRef.current === run) {
        const created: Subscription[] = []
        const failed: { event: SubscriptionEventType; message: string }[] = []
        results.forEach((r, i) => {
          if (r.status === 'fulfilled') created.push(r.value)
          else failed.push({ event: toCreate[i], message: r.reason instanceof Error ? r.reason.message : String(r.reason) })
        })
        if (created.length > 0) setSubs(prev => [...prev, ...created])
        // Only the failures stay selected, so a retry can't create duplicates of what succeeded.
        setEventTypes(failed.map(f => f.event))
        if (failed.length > 0) {
          setError(`Could not add: ${failed.map(f => `${eventLabel(f.event)} (${f.message})`).join('; ')}`)
        }
        // The destination is kept on purpose: it stays auto-populated for the next batch of events.
      }
    } finally {
      // A newer run (wallet switched meanwhile) owns the guard and the flag now.
      if (addRunRef.current === run) {
        addingRef.current = false
        setAdding(false)
      }
    }
  }

  const handleDelete = async (sub: Subscription) => {
    if (!portfolioId) return
    try {
      await deleteSubscription(portfolioId, sub.id)
      setSubs(prev => prev.filter(s => s.id !== sub.id))
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to remove subscription')
    }
  }

  const destinationPlaceholder = channel === 'TELEGRAM'
    ? '-100xxxxxxxxxx (chat ID)'
    : 'you@example.com'

  return (
    <div className="bg-surface-card rounded-xl border border-fringe border-spectral p-6">
      <h2 className="text-lg font-semibold mb-4">Notifications</h2>

      {/* Profile settings */}
      {portfolioId && profileReadyFor === portfolioId && (
        <ProfileSection key={portfolioId} portfolioId={portfolioId} profile={profile} onSaved={handleProfileSaved} />
      )}

      {error && <p className="text-accent-red text-sm mb-3">{error}</p>}
      {notice && <p className="text-amber-400 text-sm mb-3" role="status">{notice}</p>}

      {/* Add subscription form */}
      <form onSubmit={handleAdd} className="flex flex-col gap-3 mb-6">
        <h3 className="text-sm font-semibold text-ink-soft">Add Subscription</h3>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div>
            <label htmlFor="sub-event" className="text-xs text-ink-muted mb-1 block">Events</label>
            <MultiSelectDropdown
              id="sub-event"
              ariaLabel="Events"
              options={EVENT_TYPES}
              label={eventLabel}
              selected={eventTypes}
              disabledOptions={unavailable}
              disabledNotes={unavailableNotes}
              placeholder="Select events…"
              disabled={adding}
              onChange={setEventTypes}
            />
          </div>
          <div>
            <label htmlFor="sub-channel" className="text-xs text-ink-muted mb-1 block">Channel</label>
            <select
              id="sub-channel"
              name="sub-channel"
              value={channel}
              onChange={e => setChannel(e.target.value as SubscriptionChannel)}
              disabled={adding}
              className="w-full bg-surface border border-surface-card rounded-lg px-3 py-2 text-sm text-ink focus:outline-none focus:border-accent-blue disabled:opacity-50"
            >
              <option value="TELEGRAM">Telegram</option>
              <option value="EMAIL">Email</option>
            </select>
          </div>
          <div>
            <label htmlFor="sub-destination" className="text-xs text-ink-muted mb-1 block">
              {channel === 'TELEGRAM' ? 'Chat ID' : 'Email Address'}
            </label>
            <input
              id="sub-destination"
              name="sub-destination"
              type={channel === 'EMAIL' ? 'email' : 'text'}
              value={destination}
              onChange={e => {
                destinationTouchedRef.current = true
                setDestination(e.target.value)
              }}
              placeholder={destinationPlaceholder}
              disabled={adding}
              className="w-full bg-surface border border-surface-card rounded-lg px-3 py-2 text-sm text-ink placeholder-gray-600 focus:outline-none focus:border-accent-blue disabled:opacity-50"
            />
          </div>
          {cooldownApplies && (
            <div>
              <label htmlFor="sub-cooldown" className="text-xs text-ink-muted mb-1 block">Cooldown (s)</label>
              <input
                id="sub-cooldown"
                name="sub-cooldown"
                type="number"
                value={cooldown}
                onChange={e => setCooldown(Number(e.target.value))}
                min={60}
                disabled={adding}
                className="w-full bg-surface border border-surface-card rounded-lg px-3 py-2 text-sm text-ink focus:outline-none focus:border-accent-blue disabled:opacity-50"
              />
            </div>
          )}
        </div>
        <button
          type="submit"
          disabled={adding || !destination.trim() || toCreate.length === 0}
          className="self-start px-4 py-2 rounded-lg bg-accent-blue text-ink text-sm font-medium hover:bg-accent-blue/80 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {adding ? 'Adding…' : toCreate.length > 1 ? `+ Add ${toCreate.length} Subscriptions` : '+ Add Subscription'}
        </button>
      </form>

      {/* Active subscriptions */}
      {loading ? (
        <div className="space-y-2">
          {[1, 2].map(i => <div key={i} className="h-10 bg-surface rounded animate-pulse" />)}
        </div>
      ) : subs.length === 0 ? (
        <p className="text-gray-500 text-sm">No active subscriptions.</p>
      ) : (
        <div className="space-y-2">
          {subs.map(sub => (
            <div key={sub.id} className="flex items-center justify-between bg-surface rounded-lg px-4 py-2.5">
              <div>
                <span className="text-sm font-medium text-ink">{eventLabel(sub.eventType)}</span>
                <span className={`text-xs ml-2 px-1.5 py-0.5 rounded ${
                  sub.channel === 'TELEGRAM'
                    ? 'bg-blue-500/20 text-blue-300'
                    : 'bg-green-500/20 text-green-300'
                }`}>
                  {sub.channel}
                </span>
                <span className="text-xs text-ink-muted ml-2">→ {sub.destination}</span>
                <span className="text-xs text-gray-400 ml-2">{cadenceLabel(sub)}</span>
              </div>
              <button
                onClick={() => handleDelete(sub)}
                className="text-xs text-gray-500 hover:text-accent-red ml-4 transition-colors"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
